import { useEffect, useState } from "react";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import { getVisitorId, setLandingAttribution, trackEvent, trackLandingEvent, trackButtonClick, setupPageTimeTracking } from "../lib/analytics";
import { DEFAULT_LANDING_PAGE_CONTENT, type LandingPageContent } from "@shared/landingPages";
import { buildSoftwareApplicationOffers, SOFTWARE_PLANS } from "@shared/softwarePlans";
import ManagedContentSections from "../components/lp/ManagedContentSections";
import ScriptTrackMockup from "../components/lp/ScriptTrackMockup";
import TrustBadges from "../components/lp/TrustBadges";
import { SOFTWARE_META_DESCRIPTION } from "@shared/publicPageSeoBuilders";

export default function Software() {
  const [content, setContent] = useState<LandingPageContent>(DEFAULT_LANDING_PAGE_CONTENT.software);
  useEffect(() => {
    fetch(`/api/landing-pages/software?visitorId=${encodeURIComponent(getVisitorId())}`)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("managed page unavailable")))
      .then((data) => {
        if (data?.content) setContent(data.content as LandingPageContent);
        setLandingAttribution({ landingPage: "/software", variantKey: data?.assignment?.variantKey || "control", experimentId: data?.assignment?.experimentId || undefined });
        trackLandingEvent("landing_page_view", "/software");
      })
      .catch(() => {
        setLandingAttribution({ landingPage: "/software", variantKey: "control" });
        trackLandingEvent("landing_page_view", "/software");
      });
    return setupPageTimeTracking("/software");
  }, []);

  // Rolling 1-year window — keeps offer prices from appearing expired to Google.
  const priceValidUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0];

  return (
    <>
      <SEO
        title="ScriptTrack — Dispensing & DSCSA Software"
        description={SOFTWARE_META_DESCRIPTION}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Software", path: "/software" },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "ScriptTrack",
          applicationCategory: "HealthApplication",
          operatingSystem: "Web",
          description:
            "ScriptTrack starts with a free account and included starter compliance software. Optional products add inventory, dispensing, prescribing, and billing workflows.",
          url: "https://premierpharma.com/software",
          offers: buildSoftwareApplicationOffers(priceValidUntil),
          publisher: { "@id": "https://premierpharma.com/#organization" },
        }}
      />
      <section className="py-16 lg:py-20 bg-gradient-to-br from-background to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* ── Left column: outcome-led copy + CTA ─────────────────── */}
            <div className="flex flex-col gap-5 text-center lg:text-left items-center lg:items-start">
              <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full w-fit">
                Free account + included starter software
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-primary leading-tight tracking-tight">{content.headline}</h1>
              <p className="text-lg text-gray-600 max-w-xl">{content.subheadline}</p>
              <div className="flex flex-col sm:flex-row gap-4 items-center lg:items-start mt-1">
                <Link
                  to={content.ctaDestination}
                  onClick={() => trackLandingEvent("landing_page_cta_click", "/software", { buttonLabel: content.ctaText, buttonLocation: "software-hero" })}
                  className="btn-primary inline-flex items-center justify-center gap-2 px-8 py-4 text-base w-full sm:w-auto"
                >
                  {content.ctaText}
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <a
                  href="#paid-options"
                  onClick={() => trackEvent("software_plan_compare", {
                    location: "software_hero",
                  })}
                  className="inline-flex items-center justify-center text-gray-600 hover:text-primary font-semibold text-sm px-2 py-3 underline underline-offset-4 transition-colors"
                >
                  Compare optional paid products
                </a>
              </div>
              <TrustBadges variant="light" className="justify-center lg:justify-start" signals={content.trustSignals} />
              <p className="text-sm font-medium text-gray-500">{content.reassurance}</p>
            </div>

            {/* ── Right column: product proof ─────────────────────────── */}
            <div className="flex justify-center lg:justify-end">
              <ScriptTrackMockup />
            </div>
          </div>
        </div>
      </section>
       <ManagedContentSections content={content} />

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-primary text-center mb-8">Simple, Transparent Pricing</h2>
           <p className="text-center text-gray-600 max-w-2xl mx-auto mb-8">Every eligible business starts with the same free entry point. Paid products are optional add-ons for practices with more specific inventory, dispensing, prescribing, or billing needs.</p>
           <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" id="paid-options">
             {SOFTWARE_PLANS.map((plan) => (
               <div key={plan.name} className={`card relative ${plan.popular ? "ring-2 ring-accent" : ""}`}>
                {plan.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-white text-xs font-bold px-3 py-1 rounded-full">POPULAR</span>}
                <h3 className="font-bold text-primary text-lg">{plan.name}</h3>
                 <p className="text-accent text-xs font-semibold mt-2">{plan.audience}</p>
                <p className="text-gray-500 text-sm mt-1">{plan.desc}</p>
                <div className="mt-4 mb-4">
                    <span className={`${plan.included ? "text-2xl" : "text-3xl"} font-bold text-primary`}>{plan.priceLabel}</span>
                  <span className="text-gray-500 text-sm">{plan.period}</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      {f}
                    </li>
                  ))}
                </ul>
                 <Link to={content.ctaDestination} onClick={() => { const label = plan.included ? "Create a free account" : `Discuss ${plan.name}`; trackLandingEvent("landing_page_cta_click", "/software", { buttonLabel: label, buttonLocation: `pricing-card-${plan.name.toLowerCase().replace(/\s+/g, "-")}` }); trackButtonClick(label, `pricing-card-${plan.name.toLowerCase().replace(/\s+/g, "-")}`); }} className={`block text-center py-2 rounded-btn font-semibold text-sm ${plan.popular ? "btn-primary" : "btn-outline"}`}>{plan.included ? "Create a free account" : "Discuss this option"}</Link>
              </div>
            ))}
          </div>
            <p className="text-center text-gray-500 text-sm mt-6">{content.offer.optionalNextSteps} Paid plan pricing is shown for comparison and can be discussed after verification.</p>
        </div>
      </section>
    </>
  );
}
